import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Location } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { StudentJobDetail } from '@edtech-ui/core/models/student-job-management/get-all-student-job-details';
import { StudentJobService } from '@edtech-ui/core/services/student-job-management/student-job.service';
import { ToastrService } from 'ngx-toastr';
import { catchError, finalize, of, Subscription, tap } from 'rxjs';

@Component({
	selector: 'app-jobs-detail-page',
	standalone: true,
	imports: [CommonModule, MatProgressSpinnerModule],
	templateUrl: './jobs-detail-page.component.html',
	styleUrl: './jobs-detail-page.component.scss'
})
export class JobsDetailPageComponent implements OnInit, OnDestroy {

	jobId!: string;
	subscription = new Subscription();
	isLoading: boolean = false;
	isJobSave: boolean = false;
	isJobRemove: boolean = false;
	jobDetails: StudentJobDetail | null = null;

	get rolesAndResponsibilities(): string[] {
		if (!this.jobDetails?.roles_and_responsibilities) return [];
		return this.jobDetails.roles_and_responsibilities.split('\n').map(role => role.trim()).filter(role => !!role);
	}


	constructor(
		private router: Router,
		private route: ActivatedRoute,
		private studentJobService: StudentJobService,
		private toastr: ToastrService,
		private location: Location
	) { }


	ngOnInit(): void {
		this.subscription.add(
			this.route.paramMap.subscribe(params => {
				this.jobId = params.get('jobId') || '';
				if (this.jobId) {
					this.getStudentJobDetails();
				}
			})
		);
	}


	getStudentJobDetails() {
		this.isLoading = true;
		this.subscription.add(
			this.studentJobService.getAllStudentJobDetails(this.jobId).pipe(
				tap(response => {
					if (response.Success) {
						this.jobDetails = {
							...response.Data,
							locations: (response.Data.locations || []).filter(location => location?.trim())
						};
					} else {
						this.toastr.error(response.Message);
					}
				}),
				finalize(() => {
					this.isLoading = false;
				}),
				catchError(() => {
					this.toastr.error('Something went wrong. Please try again');
					return of(null);
				}),
			).subscribe()
		)
	}



	toggleSaveJob() {
		if (this.jobDetails?.is_saved) {
			this.deleteStudentSaveJob();
		} else {
			this.postStudentSaveJob();
		}
	}


	postStudentSaveJob() {
		this.isJobSave = true;
		this.subscription.add(
			this.studentJobService.postStudentSaveJob(this.jobId).pipe(
				tap(response => {
					if (response.Success) {
						this.jobDetails!.is_saved = true;
						this.toastr.success(response.Data.message);
					} else {
						this.toastr.error(response.Message);
					}
				}),
				finalize(() => {
					this.isJobSave = false;
				})
			).subscribe()
		)
	}


	deleteStudentSaveJob() {
		this.isJobRemove = true;
		this.subscription.add(
			this.studentJobService.deleteStudentSaveJob(this.jobId).pipe(
				tap(response => {
					if (response.Success) {
						this.jobDetails!.is_saved = false;
						this.toastr.success(response.Data.message);
					} else {
						this.toastr.error(response.Message);
					}
				}),
				finalize(() => {
					this.isJobRemove = false;
				})
			).subscribe()
		)
	}



	goToApplyPage(jobId: string) {
		this.router.navigate(['student-dashboard/jobs/apply', jobId])
	}

	goBack() {
		this.router.navigate(['student-dashboard/jobs'])
	}

	formatSalary(min: any, max: any, currency: string = '₹', freq: string = 'LPA'): string {
		const safeFreq = freq || 'LPA';
		const safeCurrency = currency || '₹';
		if (!min && !max) return '0';
		
		const formatSingle = (val: any) => {
			if (val === null || val === undefined || val === '') return '';
			const num = Number(val);
			if (isNaN(num)) return String(val);
			if (safeFreq === 'LPA') {
				const lpaVal = num >= 1000 ? num / 100000 : num;
				return Number(lpaVal.toFixed(2)).toString();
			}
			return `${num}`;
		};

		let displayStr = formatSingle(min) || '0';
		if (max && max !== min) {
			displayStr += ` - ${formatSingle(max)}`;
		}
		
		return `${safeCurrency} ${displayStr} ${safeFreq}`;
	}

	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}


}
